"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import type { CSSProperties } from "react";
import {
  fetchAdminBadgeCounts as apiFetchAdminBadgeCounts,
  markAdminTabRead as apiMarkAdminTabRead,
} from "@/app/lib/api";
import type { AdminBadgeCounts } from "@/app/lib/types";

const ZERO_COUNTS: AdminBadgeCounts = {
  feedback: 0,
  exports: 0,
  usage: 0,
  total: 0,
};

const POLL_INTERVAL_MS = 30_000;

type MarkableTab = "feedback" | "exports" | "usage";

interface UseAdminBadgesReturn {
  counts: AdminBadgeCounts;
  refresh: () => Promise<void>;
  markTabRead: (tab: MarkableTab) => Promise<void>;
}

/**
 * Polls `/admin/badge-counts` every 30s while `enabled` is true and the user
 * is an admin. Also refreshes on window focus. Provides an optimistic
 * `markTabRead` that zeroes the relevant field locally, calls the server,
 * and reconciles with a fresh fetch afterward.
 *
 * No-ops entirely for non-admin users — does not hit the server.
 */
export function useAdminBadges(
  enabled: boolean,
  isAdmin: boolean
): UseAdminBadgesReturn {
  const [counts, setCounts] = useState<AdminBadgeCounts>(ZERO_COUNTS);
  const countsRef = useRef<AdminBadgeCounts>(ZERO_COUNTS);
  countsRef.current = counts;

  const active = enabled && isAdmin;

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await apiFetchAdminBadgeCounts();
      setCounts(data);
    } catch {
      // Non-critical — keep prior counts.
    }
  }, [isAdmin]);

  const markTabRead = useCallback(
    async (tab: MarkableTab) => {
      if (!isAdmin) return;
      const previous = countsRef.current;
      const tabCount = previous[tab] ?? 0;
      // Optimistic local update — zero the chosen field, subtract from total.
      const optimistic: AdminBadgeCounts = {
        ...previous,
        [tab]: 0,
        total: Math.max(0, previous.total - tabCount),
      };
      setCounts(optimistic);
      try {
        await apiMarkAdminTabRead(tab);
        // Reconcile in case other notifications arrived.
        await refresh();
      } catch {
        // Roll back on failure.
        setCounts(previous);
      }
    },
    [isAdmin, refresh]
  );

  // Polling + focus-refresh effect. Only active when `enabled && isAdmin`.
  useEffect(() => {
    if (!active) {
      // Reset counts on disable so stale values don't linger after logout.
      setCounts(ZERO_COUNTS);
      return;
    }

    // Initial fetch.
    refresh();

    const intervalId = setInterval(() => {
      refresh();
    }, POLL_INTERVAL_MS);

    const onFocus = () => {
      refresh();
    };
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [active, refresh]);

  return { counts, refresh, markTabRead };
}

/** Formats a badge count — >9 becomes "9+". */
export function formatBadgeCount(n: number): string {
  if (n <= 0) return "";
  if (n > 9) return "9+";
  return String(n);
}

/**
 * Inline styles for a small red notification pill. Caller is responsible for
 * positioning (absolute within a relatively-positioned parent, or inline).
 */
export const BADGE_PILL_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "16px",
  height: "16px",
  padding: "0 4px",
  borderRadius: "8px",
  background: "#ef4444",
  color: "#fff",
  fontSize: "10px",
  fontWeight: 700,
  lineHeight: 1,
  boxSizing: "border-box",
};

